import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { GetUngatedPaginatedShopCategoryQuery } from './getUngatedPaginatedShopCategory';
export type ShopCategoryGridPaginationProps = {
  gridId?: string;
  previousGridId?: string;
  showMoreEnabled?: boolean;
};
type ShopCategoryPaginationCursor = {
  categoryId: string;
  cursor?: string | null;
};
type ShopCategoryGridCursorContextValue = {
  cursorsByGridId: Record<string, ShopCategoryPaginationCursor>;
  setCursorForGrid: (gridId: string, categoryId: string, cursor: string | null) => void;
};
const ShopCategoryGridCursorContext = createContext<ShopCategoryGridCursorContextValue | null>(null);
export const ShopCategoryGridCursorProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  // track each grid's continuation cursor so later grids for the same category can continue from it.
  const [cursorsByGridId, setCursorsByGridId] = useState<Record<string, ShopCategoryPaginationCursor>>({});
  const setCursorForGrid = useCallback((gridId: string, categoryId: string, cursor: string | null) => {
    setCursorsByGridId(currentCursors => {
      const existingCursor = currentCursors[gridId];
      if (existingCursor?.categoryId === categoryId && existingCursor.cursor === cursor) {
        return currentCursors;
      }

      // merge cursor updates because grids can complete in separate render passes.
      return {
        ...currentCursors,
        [gridId]: {
          categoryId,
          cursor
        }
      };
    });
  }, []);
  return <ShopCategoryGridCursorContext.Provider value={{
    cursorsByGridId,
    setCursorForGrid
  }}>
      {children}
    </ShopCategoryGridCursorContext.Provider>;
};
const getShopProductsContinuationCursor = (data?: GetUngatedPaginatedShopCategoryQuery) => {
  const pageInfo = data?.shopContent?.categoryPage?.category?.shopProducts?.pageInfo;
  return pageInfo?.hasNextPage ? pageInfo.endCursor ?? null : null;
};
export const useShopCategoryGridPagination = ({
  categoryId,
  gridId,
  previousGridId
}: Pick<ShopCategoryGridPaginationProps, 'gridId' | 'previousGridId'> & {
  categoryId: string;
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const {
    cursorsByGridId = {},
    setCursorForGrid
  } = useContext(ShopCategoryGridCursorContext) ?? {};
  const previousGridCursor = previousGridId ? cursorsByGridId[previousGridId] : undefined;
  const currentGridCursor = gridId ? cursorsByGridId[gridId] : undefined;
  const isPreviousCursorForSameCategory = previousGridCursor?.categoryId === categoryId;
  const paginationCursor = isPreviousCursorForSameCategory ? previousGridCursor?.cursor : undefined;

  // keep this grid in a placeholder state until the previous grid publishes any cursor result.
  const isWaitingForPreviousGridCursor = Boolean(previousGridId && !previousGridCursor);

  // a null cursor from the previous grid means the category has no more
  // products, so this grid has nothing left to load.
  const hasNoMorePreviousGridProducts = isPreviousCursorForSameCategory && paginationCursor === null;
  const setCursorFromData = useCallback((nextData?: GetUngatedPaginatedShopCategoryQuery) => {
    if (!gridId || !setCursorForGrid) {
      return;
    }
    setCursorForGrid(gridId, categoryId, getShopProductsContinuationCursor(nextData));
  }, [categoryId, gridId, setCursorForGrid]);
  useEffect(() => {
    // when the previous grid has no more products, set the cursor to null to indicate that the category has no more products
    if (!gridId || !setCursorForGrid || !hasNoMorePreviousGridProducts || currentGridCursor?.categoryId === categoryId) {
      return;
    }
    setCursorForGrid(gridId, categoryId, null);
  }, [categoryId, currentGridCursor?.categoryId, gridId, hasNoMorePreviousGridProducts, setCursorForGrid]);
  return {
    paginationCursor,
    hasNoMorePreviousGridProducts,
    isLoadingMore,
    isWaitingForPreviousGridCursor,
    setCursorFromData,
    setIsLoadingMore
  };
};